import * as React from "react"
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table"
import { Button } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { Checkbox } from "~/components/ui/checkbox"
import {
  DataTableFacetedFilter,
  type DataTableFacetOption,
} from "~/components/ui/data-table-faceted-filter"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select"
import {
  useRowSelection,
  type RowSelectionController,
} from "~/hooks/useRowSelection"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onRowClick?: (row: TData) => void
  searchColumn?: string
  searchPlaceholder?: string
  /**
   * Back-compat aliases (some pages were passing these names)
   */
  filterColumn?: string
  filterPlaceholder?: string
  /**
   * Optional controlled column filters state (useful for URL syncing).
   */
  columnFilters?: ColumnFiltersState
  onColumnFiltersChange?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>
  /**
   * Initial sort applied on first render (e.g. alphabetical by name).
   */
  defaultSorting?: SortingState
  /**
   * When set, sorting / page / page size are remembered for the session under
   * this key, so returning to the list (e.g. after opening a row) restores it.
   */
  stateKey?: string
  defaultPageSize?: number
  pageSizeOptions?: number[]
  /**
   * True lets every row be selected; a function decides per row, and a row it
   * refuses renders a disabled checkbox and is left out of "select all".
   */
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean)
  /**
   * Optional controlled selection, keyed by row id, so a page can clear or
   * reshape what is selected after a bulk action.
   */
  rowSelection?: RowSelectionState
  onRowSelectionChange?: React.Dispatch<React.SetStateAction<RowSelectionState>>
  getRowId?: (row: TData, index: number) => string
  bulkActions?: Array<{
    label: string
    onClick: (selectedRows: TData[]) => void
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    /** Shown but not clickable, with `title` saying why. */
    disabled?: boolean
    title?: string
  }>
}

type FacetMeta<TData> = {
  title?: string
  getKey: (row: TData) => string | string[] | null | undefined
  getLabel?: (row: TData, key: string) => string | null | undefined
  sort?: (a: DataTableFacetOption, b: DataTableFacetOption) => number
}

type DataTableColumnMeta<TData> = {
  facet?: FacetMeta<TData>
  sortKey?: (row: TData) => unknown
}

function compareUnknown(a: unknown, b: unknown) {
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  if (typeof a === "number" && typeof b === "number") return a - b
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime()
  return String(a).localeCompare(String(b))
}

function enrichColumns<TData, TValue>(
  cols: ColumnDef<TData, TValue>[]
): ColumnDef<TData, TValue>[] {
  return cols.map((col: any) => {
    if (col.columns) {
      return { ...col, columns: enrichColumns(col.columns) }
    }

    const meta = (col.meta ?? {}) as DataTableColumnMeta<TData>
    const facet = meta?.facet
    const sortKey = meta?.sortKey

    const next: any = { ...col, meta }

    if (facet && !next.filterFn) {
      next.filterFn = (row: any, _id: any, value: any) => {
        const selected = Array.isArray(value) ? value : value ? [value] : []
        if (selected.length === 0) return true
        const rawKey = facet.getKey(row.original)
        if (rawKey == null) return false
        const keys = Array.isArray(rawKey) ? rawKey : [rawKey]
        return keys.some((key) => selected.includes(key))
      }
    }

    if (sortKey && !next.sortingFn) {
      next.sortingFn = (rowA: any, rowB: any) => {
        const a = sortKey(rowA.original)
        const b = sortKey(rowB.original)
        return compareUnknown(a, b)
      }
    }

    return next
  })
}

type PersistedTableState = {
  sorting?: SortingState
  pagination?: PaginationState
}

const TABLE_STATE_PREFIX = "solastack.datatable."

function readPersistedState(stateKey?: string): PersistedTableState {
  if (!stateKey || typeof window === "undefined") return {}
  try {
    const raw = window.sessionStorage.getItem(TABLE_STATE_PREFIX + stateKey)
    return raw ? (JSON.parse(raw) as PersistedTableState) : {}
  } catch {
    return {}
  }
}

function writePersistedState(stateKey: string | undefined, state: PersistedTableState) {
  if (!stateKey || typeof window === "undefined") return
  try {
    window.sessionStorage.setItem(
      TABLE_STATE_PREFIX + stateKey,
      JSON.stringify(state)
    )
  } catch {
    // Session storage can be unavailable (private mode); remembering is optional.
  }
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  searchColumn,
  searchPlaceholder = "Search...",
  filterColumn,
  filterPlaceholder,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange: onControlledColumnFiltersChange,
  defaultSorting,
  stateKey,
  defaultPageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  enableRowSelection = false,
  rowSelection: controlledRowSelection,
  onRowSelectionChange: onControlledRowSelectionChange,
  getRowId,
  bulkActions = [],
}: DataTableProps<TData, TValue>) {
  const effectiveSearchColumn = searchColumn ?? filterColumn
  const effectiveSearchPlaceholder = searchPlaceholder ?? filterPlaceholder ?? "Search..."

  const enhancedColumns = React.useMemo(
    () => enrichColumns(columns),
    [columns]
  )

  const persisted = React.useMemo(() => readPersistedState(stateKey), [stateKey])

  const [sorting, setSorting] = React.useState<SortingState>(
    () => persisted.sorting ?? defaultSorting ?? []
  )
  const [pagination, setPagination] = React.useState<PaginationState>(() => ({
    pageIndex: persisted.pagination?.pageIndex ?? 0,
    pageSize: persisted.pagination?.pageSize ?? defaultPageSize,
  }))

  // A changed key means a different list (another school year, another class):
  // adopt whatever was remembered for it instead of carrying state over.
  const lastStateKey = React.useRef(stateKey)
  React.useEffect(() => {
    if (lastStateKey.current === stateKey) return
    lastStateKey.current = stateKey
    const next = readPersistedState(stateKey)
    setSorting(next.sorting ?? defaultSorting ?? [])
    setPagination({
      pageIndex: next.pagination?.pageIndex ?? 0,
      pageSize: next.pagination?.pageSize ?? defaultPageSize,
    })
  }, [stateKey, defaultSorting, defaultPageSize])

  React.useEffect(() => {
    if (lastStateKey.current !== stateKey) return
    writePersistedState(stateKey, { sorting, pagination })
  }, [stateKey, sorting, pagination])
  const [uncontrolledColumnFilters, setUncontrolledColumnFilters] =
    React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [uncontrolledRowSelection, setUncontrolledRowSelection] =
    React.useState<RowSelectionState>({})
  const rowSelection = controlledRowSelection ?? uncontrolledRowSelection
  const setRowSelection = onControlledRowSelectionChange ?? setUncontrolledRowSelection

  const columnFilters = controlledColumnFilters ?? uncontrolledColumnFilters
  const setColumnFilters =
    onControlledColumnFiltersChange ?? setUncontrolledColumnFilters

  const table = useReactTable({
    data,
    columns: enhancedColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    enableRowSelection,
    getRowId:
      getRowId ??
      ((row: TData, index: number) => {
        const id = (row as { id?: string | number } | null)?.id
        return id == null ? String(index) : String(id)
      }),
  })

  // A remembered page can fall past the end once filtering or new data changes
  // the row count; snap back rather than showing an empty table.
  const pageCount = table.getPageCount()
  React.useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex > pageCount - 1) {
      table.setPageIndex(pageCount - 1)
    }
  }, [pageCount, pagination.pageIndex, table])

  const visibleRowIds = table
    .getFilteredRowModel()
    .rows.filter((row) => row.getCanSelect())
    .map((row) => row.id)
  const selectionController = useRowSelection({
    visibleRowIds,
    rowSelection,
    setRowSelection,
  })

  const facetColumns = React.useMemo(() => {
    return enhancedColumns
      .map((col: any) => {
        const meta = col?.meta as DataTableColumnMeta<TData> | undefined
        if (!meta?.facet) return null
        const id =
          col.id ??
          (typeof col.accessorKey === "string" ? col.accessorKey : undefined)
        if (!id) return null
        return { id: String(id), facet: meta.facet }
      })
      .filter(Boolean) as Array<{ id: string; facet: FacetMeta<TData> }>
  }, [enhancedColumns])

  const facetOptionsByColumnId = React.useMemo(() => {
    const out: Record<string, DataTableFacetOption[]> = {}

    for (const { id, facet } of facetColumns) {
      const seen = new Map<string, string>()
      for (const row of data) {
        const rawKey = facet.getKey(row)
        const keys = Array.isArray(rawKey) ? rawKey : rawKey ? [rawKey] : []
        for (const key of keys) {
          if (!key) continue
          const label = (facet.getLabel?.(row, key) ?? key) || key
          if (!seen.has(key)) seen.set(key, label)
        }
      }

      const opts = Array.from(seen.entries()).map(([value, label]) => ({
        value,
        label,
      }))

      opts.sort(
        facet.sort ??
          ((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
      )

      out[id] = opts
    }

    return out
  }, [data, facetColumns])

  const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original)

  return (
    <DataTableSelectionContext.Provider value={selectionController}>
    <div className="w-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between py-4">
        <div className="flex flex-wrap items-center gap-2">
          {effectiveSearchColumn && (
            <Input
              placeholder={effectiveSearchPlaceholder}
              value={
                (table.getColumn(effectiveSearchColumn)?.getFilterValue() as string) ??
                ""
              }
              onChange={(event) =>
                table
                  .getColumn(effectiveSearchColumn)
                  ?.setFilterValue(event.target.value)
              }
              className="max-w-sm"
            />
          )}

          {facetColumns.map(({ id, facet }) => {
            const column = table.getColumn(id)
            if (!column) return null

            const raw = column.getFilterValue()
            const selected = new Set<string>(
              Array.isArray(raw) ? (raw as string[]) : raw ? [String(raw)] : []
            )

            const title =
              facet.title ??
              (typeof column.columnDef.header === "string"
                ? (column.columnDef.header as string)
                : id)

            const options = facetOptionsByColumnId[id] ?? []

            return (
              <DataTableFacetedFilter
                key={id}
                title={title}
                options={options}
                selectedValues={selected}
                onSelectedValuesChange={(next) => {
                  const values = Array.from(next)
                  column.setFilterValue(values.length ? values : undefined)
                }}
              />
            )
          })}

          {columnFilters.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => table.resetColumnFilters()}
              className="h-9"
            >
              Reset
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {enableRowSelection && selectedRows.length > 0 && bulkActions.length > 0 && (
            <>
              <span className="text-sm text-muted-foreground">
                {selectedRows.length} selected
              </span>
              {bulkActions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || "outline"}
                  size="sm"
                  onClick={() => action.onClick(selectedRows)}
                  disabled={action.disabled}
                  title={action.title}
                >
                  {action.label}
                </Button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={(e) => {
                    // Don't navigate if clicking on checkbox or action button
                    const target = e.target as HTMLElement
                    if (
                      target.closest('[role="checkbox"]') ||
                      target.closest('button') ||
                      target.closest('[data-no-row-click]')
                    ) {
                      return
                    }
                    onRowClick?.(row.original)
                  }}
                  className={onRowClick ? "cursor-pointer" : ""}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-muted-foreground">
          {enableRowSelection && table.getFilteredSelectedRowModel().rows.length > 0 ? (
            <>
              {table.getFilteredSelectedRowModel().rows.length} of{" "}
              {table.getFilteredRowModel().rows.length} row(s) selected.
            </>
          ) : (
            <>{table.getFilteredRowModel().rows.length} result(s)</>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              Rows per page
            </span>
            <Select
              value={String(pagination.pageSize)}
              onValueChange={(value) => table.setPageSize(Number(value))}
            >
              <SelectTrigger className="h-9 w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            Page {table.getPageCount() === 0 ? 0 : pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              aria-label="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              aria-label="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

    </div>
    </DataTableSelectionContext.Provider>
  )
}

const DataTableSelectionContext = React.createContext<RowSelectionController | null>(null)

function SelectAllVisibleCheckbox({ table }: { table: any }) {
  const selection = React.useContext(DataTableSelectionContext)

  if (!selection) {
    return (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all visible rows"
      />
    )
  }

  return (
    <Checkbox
      checked={
        selection.allVisibleSelected ||
        (selection.someVisibleSelected && "indeterminate")
      }
      onCheckedChange={(value) => selection.toggleAllVisible(!!value)}
      aria-label="Select all visible rows"
    />
  )
}

function SelectRowCheckbox({
  row,
  disabledTitle,
}: {
  row: any
  disabledTitle?: (row: any) => string | null | undefined
}) {
  const selection = React.useContext(DataTableSelectionContext)
  const shiftKey = React.useRef(false)
  const canSelect = row.getCanSelect()

  return (
    <Checkbox
      checked={row.getIsSelected()}
      disabled={!canSelect}
      title={(!canSelect && disabledTitle?.(row.original)) || undefined}
      onClick={(event) => {
        shiftKey.current = event.shiftKey
      }}
      onCheckedChange={(value) => {
        if (selection) selection.toggleRow(row.id, !!value, shiftKey.current)
        else row.toggleSelected(!!value)
        shiftKey.current = false
      }}
      aria-label={`Select row ${row.id}`}
    />
  )
}

// Helper component for sortable column headers
interface SortableHeaderProps {
  column: any
  children: React.ReactNode
}

export function SortableHeader({ column, children }: SortableHeaderProps) {
  const sorted = column.getIsSorted()
  
  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      className="-ml-4"
    >
      {children}
      {sorted === "asc" ? (
        <ChevronUp className="ml-2 h-4 w-4" />
      ) : sorted === "desc" ? (
        <ChevronDown className="ml-2 h-4 w-4" />
      ) : (
        <ChevronsUpDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  )
}

// Helper to create a select column for row selection. `disabledTitle` names,
// on the checkbox itself, why a row that cannot be selected cannot be.
export function createSelectColumn<TData>(options: {
  disabledTitle?: (row: TData) => string | null | undefined
} = {}): ColumnDef<TData> {
  return {
    id: "select",
    header: ({ table }) => <SelectAllVisibleCheckbox table={table} />,
    cell: ({ row }) => <SelectRowCheckbox row={row} disabledTitle={options.disabledTitle} />,
    enableSorting: false,
    enableHiding: false,
  }
}
